import { Button, Spinner } from "@heroui/react";
import { AVATAR_CONTENT_TYPE_VALUES } from "@storyline/shared";
import { useRef } from "react";
import PageHeader from "@/src/common/components/page-header";
import SectionCard from "@/src/common/components/section-card";
import McpTokensSection from "@/src/settings/components/mcp-tokens-section";
import { useAvatarCatalog } from "@/src/settings/hooks/use-avatar-catalog";

const AVATAR_ACCEPT = AVATAR_CONTENT_TYPE_VALUES.join(",");
const RECOMMENDED_COUNT = 6;

const formatDate = (value: string): string =>
	new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });

const SettingsPage: React.FC = () => {
	const { avatars, isLoading, isUploading, hasError, error, upload, remove } = useAvatarCatalog();
	const fileInputRef = useRef<HTMLInputElement>(null);

	const onFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const file = event.target.files?.[0];
		if (file) {
			void upload(file);
		}
		event.target.value = "";
	};

	const onRemove = (id: string): void => {
		if (window.confirm("Supprimer définitivement cet avatar ?")) {
			void remove(id);
		}
	};

	return (
		<div className="flex flex-col gap-6">
			<PageHeader title="Paramètres" description="Configuration de l'application Storyline." />

			<SectionCard
				title="Avatars du profil"
				description="Avatars proposés aux utilisateurs de l'application mobile."
				actions={
					<>
						<input ref={fileInputRef} type="file" accept={AVATAR_ACCEPT} className="hidden" onChange={onFileChange} />
						<span className="text-muted text-xs">
							{avatars.length}/{RECOMMENDED_COUNT} recommandés
						</span>
						<Button isDisabled={isUploading} onPress={() => fileInputRef.current?.click()}>
							{isUploading ? "Envoi…" : "Ajouter un avatar"}
						</Button>
					</>
				}
			>
				{error ? <p className="text-danger text-sm">{error}</p> : null}

				{isLoading ? (
					<div className="flex justify-center py-12">
						<Spinner size="lg" />
					</div>
				) : hasError ? (
					<p className="rounded-xl border border-border-soft bg-surface p-10 text-center text-danger text-sm">
						Impossible de charger les avatars.
					</p>
				) : avatars.length === 0 ? (
					<p className="rounded-xl border border-border-soft border-dashed bg-glass p-10 text-center text-muted text-sm">
						Aucun avatar pour le moment. Ajoutez-en 5 à 6 pour que les utilisateurs puissent personnaliser leur profil.
					</p>
				) : (
					<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
						{avatars.map((avatar) => (
							<div key={avatar.id} className="flex flex-col items-center gap-2">
								<img src={avatar.url} alt="Avatar" className="h-24 w-24 rounded-full border border-border-soft object-cover" />
								<span className="text-muted text-xs">{formatDate(avatar.createdAt)}</span>
								<Button size="sm" variant="secondary" onPress={() => onRemove(avatar.id)}>
									Supprimer
								</Button>
							</div>
						))}
					</div>
				)}
			</SectionCard>

			<McpTokensSection />
		</div>
	);
};

export default SettingsPage;
